import React, { useState } from "react";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { supabase } from "@/integrations/supabase/client";

const APP_STORE_URL = "https://apps.apple.com/us/app/help-genie-voice/id6755985515";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.helpgenie.app";

export default function HomeFooter() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage({ type: "error", text: "Please enter a valid email" });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const { data, error } = await supabase.functions.invoke("hubspot-handler", {
        body: {
          action: "addCustomer",
          email,
          accountType: "consumer",
        },
      });

      if (error) throw error;

      setMessage({ type: "success", text: "Successfully subscribed! Check your email." });
      setEmail("");
    } catch (err) {
      console.error("Subscription error:", err);
      setMessage({ type: "error", text: "Failed to subscribe. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="w-full bg-white border-t border-gray-200">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <HelpGenieLogo size={32} />
              <span className="text-2xl font-bold text-black">Help Genie</span>
            </div>
            <p className="text-gray-600 mb-6 max-w-sm">
              Support that runs itself. Help Genie answers, engages, and converts every conversation, day or night.
            </p>
            {/* Newsletter Signup */}
            <div className="max-w-sm">
              <h4 className="text-sm font-semibold mb-3 text-gray-900">Stay Updated</h4>
              <form
                name="helpgenie_newsletter_footer"
                id="helpgenie_newsletter_footer"
                data-hs-do-not-collect="true"
                className="flex flex-col sm:flex-row gap-2"
                onSubmit={handleSubscribe}
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-genie-primary focus:ring-1 focus:ring-genie-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:opacity-90 rounded-lg text-sm font-medium text-white transition-colors sm:whitespace-nowrap disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Subscribing..." : "Subscribe"}
                </button>
                {message && <p className={`text-xs ${message.type === "success" ? "text-green-600" : "text-red-600"}`}>{message.text}</p>}
              </form>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download Help Genie on the App Store"
                  className=" inline-flex"
                >
                  <img
                    src="/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                    alt="Download on the App Store"
                    className="h-10 w-auto"
                    loading="lazy"
                  />
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Help Genie on Google Play"
                  className="inline-flex"
                >
                  <img
                    src="/images/google-play-badge.png"
                    alt="Get it on Google Play"
                    className="h-[58px] w-auto "
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Discover - Highlighted */}
          <div>
            <a
              href="/discover"
              className="inline-flex items-center gap-2 font-semibold mb-2 text-genie-primary hover:text-genie-secondary transition-colors"
            >
              Discover
            </a>
            <a href="/discover" className="block text-sm text-genie-primary hover:text-genie-secondary transition-colors mb-3">
              Find your perfect Genie
            </a>
            <ul className="space-y-2">
              <li>
                <a href="/explore" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Experience
                </a>
              </li>
              <li>
                <a href="/how-it-works" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  How It Works
                </a>
              </li>
              <li>
                <a href="/help-genie-for-business-solutions" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Solutions
                </a>
              </li>
              <li>
                <a href="/playbooks" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Playbooks
                </a>
              </li>
              <li>
                <a href="/pricing" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Pricing
                </a>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-semibold mb-4 text-gray-900">Products</h3>
            <ul className="space-y-2">
              <li>
                <a href="/products/after-hours" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  After Hours
                </a>
              </li>
              <li>
                <a href="/help-genie-for-business-solutions" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  For Business
                </a>
              </li>
              <li>
                <a href="/help-genie-for-developers" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  For Developers
                </a>
              </li>
              <li>
                <a href="/help-genie-for-partners" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  For Partners
                </a>
              </li>
              <li>
                <a href="/help-genie-for-consumers" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  For Consumers
                </a>
              </li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="font-semibold mb-4 text-gray-900">Solutions</h3>
            <ul className="space-y-2">
              <li>
                <a href="/automotive" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Automotive
                </a>
              </li>
              <li>
                <a href="/marine" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Marine
                </a>
              </li>
              <li>
                <a href="/solutions/equipment" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Equipment
                </a>
              </li>
              <li>
                <a href="/appliances" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Appliances
                </a>
              </li>
              <li>
                <a href="/office-equipment" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Office Equipment
                </a>
              </li>
              <li>
                <a href="/manufacturing" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Manufacturing
                </a>
              </li>
              <li>
                <a href="/industrial" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Industrial
                </a>
              </li>
              <li>
                <a href="/solutions/mining" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Mining
                </a>
              </li>
              <li>
                <a href="/home-builders" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Home Builders
                </a>
              </li>
              <li>
                <a href="/travel-hospitality" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Travel & Hospitality
                </a>
              </li>
              <li>
                <a href="/trades" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Trades & Services
                </a>
              </li>
              <li>
                <a href="/real-estate" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Real Estate
                </a>
              </li>
              <li>
                <a href="/solutions/clubs-associations" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Clubs & Associations
                </a>
              </li>
              <li>
                <a href="/events" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Events
                </a>
              </li>
            </ul>
          </div>

          {/* Get Started Links */}
          <div>
            <h3 className="font-semibold mb-4 text-gray-900">Get Started</h3>
            <ul className="space-y-2">
              <li>
                <a href="/signin" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Sign In
                </a>
              </li>
              <li>
                <a href="/live-agent/business-onboarding" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Get Demo
                </a>
              </li>
              <li>
                <a href="/contact" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Contact Sales
                </a>
              </li>
              <li>
                <a href="/docs" className="text-gray-600 hover:text-genie-primary transition-colors text-sm">
                  Developers
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">© {new Date().getFullYear()} Help Genie. All rights reserved.</div>
            <div className="flex gap-6 text-sm">
              <a href="/privacy" className="text-gray-500 hover:text-genie-primary transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-500 hover:text-genie-primary transition-colors">
                Terms of Service
              </a>
              <a href="/cookies" className="text-gray-500 hover:text-genie-primary transition-colors">
                Cookie Policy
              </a>
            </div>
            <div className="flex gap-4">
              <a href="#" className="text-gray-400 hover:text-genie-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-genie-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-genie-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
